// @ts-nocheck

import { useState } from "react";
import { supabaseClient } from "@/lib/supabase/client"
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { createBrowserSupabaseClient } from "@/lib/supabase/client";
import { formatTimeAgo } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { IconBell } from "@/components/ui/Icons";
import type { Notification } from "@/types";

interface NotificationsClientProps {
  notifications: Notification[];
  userId: string;
}

export function NotificationsClient({ notifications: initial, userId }: NotificationsClientProps) {
  const [notifs, setNotifs] = useState<Notification[]>(initial);
  const supabase = supabaseClient;

  const markAllRead = async () => {
    // @ts-ignore
    await supabase.from("notifications").update({ is_read: true } as any).eq("user_id", userId).eq("is_read", false);
    setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const markRead = async (id: string) => {
    // @ts-ignore
    await supabase.from("notifications").update({ is_read: true } as any).eq("id", id);
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  const unreadCount = notifs.filter(n => !n.is_read).length;

  const typeIcons: Record<string, string> = {
    post_reply: "💬", comment_reply: "↩️", vote_milestone: "↑",
    mention: "@", follow: "+", post_featured: "★",
    verification_approved: "✓", system: "·",
  };

  const typeBg: Record<string, string> = {
    post_reply: "bg-brand/15 text-brand", comment_reply: "bg-brand/15 text-brand",
    vote_milestone: "bg-success/15 text-success", mention: "bg-warning/15 text-warning",
    follow: "bg-accent/15 text-accent", post_featured: "bg-amber-400/15 text-amber-500",
    verification_approved: "bg-success/15 text-success", system: "bg-muted text-text-muted",
  };

  if (notifs.length === 0) {
    return (
      <div className="flex flex-col items-center py-20 text-center">
        <div className="w-16 h-16 rounded-3xl bg-muted flex items-center justify-center mb-4">
          <IconBell className="w-8 h-8 text-text-subtle" />
        </div>
        <p className="text-lg font-semibold text-foreground">No notifications yet</p>
        <p className="text-sm text-text-muted mt-2">When someone interacts with your content, you'll see it here.</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      {unreadCount > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm text-text-muted">{unreadCount} unread</span>
          <button onClick={markAllRead} className="text-xs text-brand hover:text-brand-hover font-medium transition-colors">
            Mark all as read
          </button>
        </div>
      )}

      {/* Notifications */}
      <div>
        {notifs.map((notif, i) => (
          <motion.div key={notif.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.02 }}>
            <Link to={notif.action_url} onClick={() => !notif.is_read && markRead(notif.id)}
              className={cn(
                "flex gap-3 px-4 py-4 hover:bg-muted transition-colors duration-150",
                "border-b border-border last:border-0",
                !notif.is_read && "bg-brand-muted/30"
              )}>
              <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold",
                typeBg[notif.type] || "bg-muted text-text-muted")}>
                {typeIcons[notif.type] || "·"}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn("text-sm leading-snug", !notif.is_read ? "font-medium text-foreground" : "text-text-muted")}>
                  {notif.title}
                </p>
                {notif.body && <p className="text-xs text-text-subtle mt-0.5 line-clamp-2">{notif.body}</p>}
                <p className="text-2xs text-text-subtle mt-1">{formatTimeAgo(notif.created_at)}</p>
              </div>
              {!notif.is_read && <div className="w-2 h-2 rounded-full bg-brand flex-shrink-0 mt-2" />}
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
